import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch {
      // silent
    }
  }, [token]);

  // Connect WebSocket for real-time notifications
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const connectWs = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/user/${user.id}`);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'order_status') {
            // Add to local state
            const newNotif = {
              id: `ws-${Date.now()}`,
              type: 'order_status',
              title: `Siparis #${data.order_id.slice(-8).toUpperCase()}`,
              message: `Siparisiniz: ${data.status_label}`,
              order_id: data.order_id,
              status: data.status,
              is_read: false,
              created_at: data.timestamp
            };
            setNotifications(prev => [newNotif, ...prev]);
            setUnreadCount(prev => prev + 1);

            // Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              try {
                new Notification('Siparis Guncellendi!', {
                  body: `Siparisiniz: ${data.status_label}`,
                  icon: '/favicon.ico',
                  tag: data.order_id
                });
              } catch {}
            }
          }
        };

        ws.onclose = () => {
          reconnectTimerRef.current = setTimeout(connectWs, 5000);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {}
    };

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    connectWs();
    fetchNotifications();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [isAuthenticated, user?.id, fetchNotifications]);

  const markAllRead = async () => {
    if (!token) return;
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const markRead = async (notifId) => {
    if (!token) return;
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAllRead, markRead, fetchNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) return { notifications: [], unreadCount: 0, markAllRead: () => {}, markRead: () => {}, fetchNotifications: () => {} };
  return ctx;
};
